import React, { useState, useEffect } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, ShoppingCart, FileText, Minus, Plus } from "lucide-react";
import useCountryPricing from '../hooks/useCountryPricing';
import { COUNTRIES } from '../lib/shippingCountries';
import ProductSlideshow from '../components/guns/ProductSlideshow';
import { GUN_CATALOG, COLORED_VARIANTS } from '../lib/gunCatalog';
import { getPendingGunOrder } from '../lib/pendingGunOrder';

import halfBlackImg from "@assets/black_halfbreed_1781116012975.jpg";
import halfUpgradeImg from "@assets/halfbreed_1791995942221.jpg";
import halfTransparentImg from "@assets/halftransparent_1772951020313.jpeg";
import halfWhiteImg from "@assets/halfbreed_white_1792017321600.jpg";
import halfRedImg from "@assets/0_9614_1792016795130.jpg";
import halfBlueImg from "@assets/3_9610_1792016795203.jpg";
import halfGreenImg from "@assets/halfbreed_green_1792017321482.jpg";

import skinnyBlackImg from "@assets/black_skinny_glock_1781116012977.jpg";
import skinnyUpgradeImg from "@assets/skinny_1791995942268.jpg";
import skinnyTransparentImg from "@assets/skinnytransparent_1772951020317.jpeg";
import skinnyWhiteImg from "@assets/skinnyglock_white_1792017321641.jpg";
import skinnyRedImg from "@assets/4_9611_1792016795231.jpg";
import skinnyBlueImg from "@assets/7_9607_1792016795287.jpg";
import skinnyGreenImg from "@assets/skinnyglock_green_1792017321541.jpg";

import fatBlackImg from "@assets/black_fat_glock_1781116012974.jpg";
import fatUpgradeImg from "@assets/fat_1791995942306.jpg";
import fatTransparentImg from "@assets/fattransparent_1772951020310.jpeg";
import fatWhiteImg from "@assets/fatglock_white_1792017321579.jpg";
import fatRedImg from "@assets/1_9613_1792016795154.jpg";
import fatBlueImg from "@assets/6_9608_1792016795269.jpg";
import fatGreenImg from "@assets/fatglock_green_1792017321519.jpg";

import halfUpgradeBlackImg from "@assets/halfbreed_black_1792009542954.png";
import halfUpgradeGreenImg from "@assets/halfbreed_green_1792009542971.png";
import halfUpgradeRedImg from "@assets/halfbreed_red_1792009542991.png";
import halfUpgradeBlueImg from "@assets/halfbreed_blue_1792009543013.jpg";
import skinnyUpgradeBlackImg from "@assets/skinny_black_1792009542877.jpg";
import skinnyUpgradeGreenImg from "@assets/skinny_green_1792009542894.jpg";
import skinnyUpgradeRedImg from "@assets/skinny_red_1792009542917.jpg";
import skinnyUpgradeBlueImg from "@assets/skinny_blue_1792009542936.jpg";
import fatUpgradeBlackImg from "@assets/fat_black_1792009542664.jpg";
import fatUpgradeGreenImg from "@assets/fat_green_1792009542789.jpg";
import fatUpgradeRedImg from "@assets/fat_red_1792009542810.jpg";
import fatUpgradeBlueImg from "@assets/fat_blue_1792009542844.jpg";

import revolverBlackImg from "@assets/black_revolver_1781116012976.jpg";
import revolverWhiteImg from "@assets/reolver_white_1792017321625.jpg";
import revolverRedImg from "@assets/2_9612_1792016795179.jpg";
import revolverBlueImg from "@assets/5_9609_1792016795255.jpg";
import revolverGreenImg from "@assets/revolver_green_1792017321456.jpg";

import uziBlackImg from "@assets/black_uzi_1781116012977.jpg";
import uziWhiteImg from "@assets/uzi_white_1792017321559.jpg";
import uziRedImg from "@assets/8_9615_1792016795309.jpg";
import uziBlueImg from "@assets/9_9606_1792016795327.jpg";
import uziGreenImg from "@assets/uzi_green_1792017321501.jpg";

import suppressorImg from "@assets/supressor_1772951020318.PNG";

const LOGO_URL = "/v4logo.png";

const UPGRADE_IMAGES = {
  1: {
    Black: halfUpgradeBlackImg,
    'Translucent White': halfUpgradeImg,
    'Translucent Red': halfUpgradeRedImg,
    'Translucent Blue': halfUpgradeBlueImg,
    'Translucent Green': halfUpgradeGreenImg,
  },
  2: {
    Black: skinnyUpgradeBlackImg,
    'Translucent White': skinnyUpgradeImg,
    'Translucent Red': skinnyUpgradeRedImg,
    'Translucent Blue': skinnyUpgradeBlueImg,
    'Translucent Green': skinnyUpgradeGreenImg,
  },
  3: {
    Black: fatUpgradeBlackImg,
    'Translucent White': fatUpgradeImg,
    'Translucent Red': fatUpgradeRedImg,
    'Translucent Blue': fatUpgradeBlueImg,
    'Translucent Green': fatUpgradeGreenImg,
  },
};

const products = [
  { id: 1, name: "Halfbreed", price: 24.99, coloredPrice: 29.99, description: "Engineered to work with various gun mods ie the Draco, AK47/u, etc. See Resources.", image: halfBlackImg, imageBlack: halfBlackImg, imageTransparent: halfTransparentImg, imageWhite: halfWhiteImg, imageRed: halfRedImg, imageBlue: halfBlueImg, imageGreen: halfGreenImg },
  { id: 2, name: "Skinny Glock", price: 49.99, coloredPrice: 54.99, description: "Made for small to medium hands. Compatible upgrades (sold separately): Suppressor, laser sight, and carbine assault rifle. See Resources.", image: skinnyBlackImg, imageBlack: skinnyBlackImg, imageTransparent: skinnyTransparentImg, imageWhite: skinnyWhiteImg, imageRed: skinnyRedImg, imageBlue: skinnyBlueImg, imageGreen: skinnyGreenImg },
  { id: 3, name: "Fat Glock", price: 49.99, coloredPrice: 54.99, description: "Made for larger hands. Compatible upgrades (sold separately): Suppressor, laser sight, and carbine assault rifle. See Resources.", image: fatBlackImg, imageBlack: fatBlackImg, imageTransparent: fatTransparentImg, imageWhite: fatWhiteImg, imageRed: fatRedImg, imageBlue: fatBlueImg, imageGreen: fatGreenImg },
  { id: 5, name: "Revolver", price: 59.99, coloredPrice: 64.99, description: "Classic. Vintage. Go back in time with this iconic firearm controller.", image: revolverBlackImg, imageBlack: revolverBlackImg, imageWhite: revolverWhiteImg, imageRed: revolverRedImg, imageBlue: revolverBlueImg, imageGreen: revolverGreenImg },
  { id: 4, name: "Uzi", price: 79.99, coloredPrice: 84.99, description: "The Big Boss controller. Compatible with suppressor attachment (sold separately).", image: uziBlackImg, imageBlack: uziBlackImg, imageWhite: uziWhiteImg, imageRed: uziRedImg, imageBlue: uziBlueImg, imageGreen: uziGreenImg },
  { id: 6, name: "Suppressor", price: 4.99, description: "Addon for the Skinny Glock, Fat Glock, and Uzi.", image: suppressorImg, imageBlack: suppressorImg }
].map(product => ({ ...product, ...GUN_CATALOG.find(item => item.id === product.id) }));

const CART_KEY = 'guns_cart';
const CART_EXPIRY_MS = 3 * 60 * 60 * 1000;

function getCart() {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || '[]'); } catch { return []; }
}
function saveCart(cart) {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
}

async function expireOldCartItems() {
  if (getPendingGunOrder()) return { valid: getCart(), expiredCount: 0 };
  const cart = getCart();
  const now = Date.now();
  const expired = cart.filter(i => i.addedAt && (now - i.addedAt) >= CART_EXPIRY_MS);
  const valid = cart.filter(i => !i.addedAt || (now - i.addedAt) < CART_EXPIRY_MS);

  for (const item of expired) {
    try {
      await fetch('/api/inventory/increment-one', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: item.id, color: item.color, quantity: item.quantity })
      });
    } catch {}
  }

  if (expired.length > 0) {
    saveCart(valid);
  }
  return { valid, expiredCount: expired.length };
}

export default function Guns() {
  usePageMeta({
    title: 'V4LLUED Guns | Joy-Con Gun Controllers for Fitness Gaming | Active Gaming',
    description: 'Shop V4LLUED Guns — Joy-Con gun controllers designed for immersive fitness gaming. Motion-controlled shooting for first-person games. Multiple styles and colours available.',
    ogImage: `${window.location.origin}/gun_figure.jpg`,
    ogUrl: window.location.origin + '/Guns',
  });

  const [productColors, setProductColors] = useState({});
  const [productQtys, setProductQtys] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [inventory, setInventory] = useState({});
  const [cart, setCart] = useState(getCart());
  const [notification, setNotification] = useState(null);
  const [user, setUser] = useState(null);
  const { selectedCountry, setSelectedCountry, currency, ready, isLoading, error, retry, rateUpdatedAt, formatPrice } = useCountryPricing();

  useEffect(() => {
    expireOldCartItems().then(({ valid, expiredCount }) => {
      if (expiredCount > 0) {
        setCart(valid);
        setNotification({ type: 'error', msg: `${expiredCount} item(s) removed from cart (expired after 3 hours)` });
        setTimeout(() => setNotification(null), 4000);
      }
    });
    fetch('/api/inventory')
      .then(res => res.json())
      .then(items => {
        const map = {};
        items.forEach(item => { map[item.product_id] = { black: item.quantity_black ?? 0, transparent: item.quantity_transparent ?? 0, red: item.quantity_red ?? 0, blue: item.quantity_blue ?? 0, green: item.quantity_green ?? 0 }; });
        setInventory(map);
      })
      .catch(() => {});
  }, []);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const refreshInventory = () => {
    fetch('/api/inventory')
      .then(res => res.json())
      .then(items => {
        const map = {};
        items.forEach(item => { map[item.product_id] = { black: item.quantity_black ?? 0, transparent: item.quantity_transparent ?? 0, red: item.quantity_red ?? 0, blue: item.quantity_blue ?? 0, green: item.quantity_green ?? 0 }; });
        setInventory(map);
      })
      .catch(() => {});
  };

  const handleAddToCart = async (product) => {
    if (getPendingGunOrder()) {
      setNotification({ type: 'error', msg: 'A checkout is saved. Return to Checkout to confirm or cancel it before changing your cart.' });
      return;
    }
    const color = productColors[product.id];
    if (!color) {
      setNotification({ type: 'error', msg: 'Please select a color first' });
      setTimeout(() => setNotification(null), 2500);
      return;
    }
    const qty = productQtys[product.id] || 1;

    try {
      const res = await fetch('/api/inventory/decrement-one', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: product.id, color, quantity: qty })
      });
      if (!res.ok) {
        const err = await res.json();
        setNotification({ type: 'error', msg: err.error || 'Failed to reserve stock' });
        setTimeout(() => setNotification(null), 2500);
        refreshInventory();
        return;
      }
    } catch {
      setNotification({ type: 'error', msg: 'Failed to connect to server' });
      setTimeout(() => setNotification(null), 2500);
      return;
    }

    const newCart = [...getCart()];
    const existing = newCart.find(i => i.id === product.id && i.color === color);
    if (existing) {
      existing.quantity += qty;
      existing.addedAt = Date.now();
    } else {
      const itemPrice = (COLORED_VARIANTS.includes(color) && product.coloredPrice) ? product.coloredPrice : product.price;
      newCart.push({ id: product.id, name: product.name, price: itemPrice, color, quantity: qty, addedAt: Date.now() });
    }
    saveCart(newCart);
    setCart([...newCart]);
    refreshInventory();
    setNotification({ type: 'success', msg: `${product.name} (${color}) ×${qty} added to cart` });
    setTimeout(() => setNotification(null), 2500);
  };

  const handleColorChange = (productId, color) => {
    setProductColors(prev => ({...prev, [productId]: color}));
  };

  const handleQtyChange = (productId, delta, maxStock) => {
    setProductQtys(prev => {
      const current = prev[productId] || 1;
      const next = current + delta;
      const clamped = Math.max(1, maxStock !== null && maxStock !== undefined ? Math.min(next, maxStock) : next);
      return { ...prev, [productId]: clamped };
    });
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-white">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-5 py-3 rounded-xl text-sm font-semibold shadow-2xl ${notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
            {notification.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="border-b border-white/5 bg-[#0B0C10]/80 backdrop-blur-xl">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="VSteps" className="h-8 sm:h-10 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="border-[#333] text-white hover:bg-white/5 bg-transparent">
              Home
            </Button>
            <Button
              onClick={() => window.location.href = '/gunscheckout'}
              className="relative bg-[#FF0000] hover:bg-[#FF0000]/90 text-white font-semibold">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Checkout
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-white text-[#FF0000] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            <div className="mt-4 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-red-500/40 blur-[60px] scale-110 pointer-events-none" />
                <img
                  src="/vguns_hero_logo.png"
                  alt="V4llued Guns Logo"
                  className="relative w-32 h-32 object-contain"
                />
              </div>
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold bg-gradient-to-r from-white via-white to-[#C5C6C7] bg-clip-text text-transparent">
              V4llued Guns{' '}
            </h1>
            <p className="mt-6 text-base max-w-2xl mx-auto">
              <span className="text-[#C5C6C7]">3D-printed joycon housing technology designed to translate your hand movement, in-game.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-4 px-4 sm:px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <p className="mb-6 text-center text-sm font-semibold text-[#FF0000]">
            Free shipping anywhere in the USA
          </p>
          <div className="max-w-md mx-auto mb-8 rounded-xl border border-white/10 bg-[#151515] p-4">
            <label htmlFor="guns-country-selector" className="mb-2 block text-sm font-semibold text-white">Choose your country</label>
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger id="guns-country-selector" data-testid="guns-country-selector" className="bg-[#0B0C10] border-white/20 text-white">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {COUNTRIES.map(option => <SelectItem key={option.code} value={option.code}>{option.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-2 flex min-h-5 items-center justify-between gap-3 text-xs text-[#C5C6C7]">
              <span>{isLoading ? 'Loading exchange rate…' : error ? 'Prices unavailable' : `Prices shown in ${currency}`}</span>
              {error && <button type="button" onClick={retry} className="font-semibold text-[#FF4040] underline">Retry</button>}
              {!error && ready && rateUpdatedAt && <span>Rate updated {new Date(rateUpdatedAt).toLocaleDateString()}</span>}
            </div>
            {error && <p role="alert" className="mt-1 text-xs text-red-300">{error}</p>}
            <a className="mt-2 inline-block text-[11px] text-[#888] underline hover:text-white" href="https://www.exchangerate-api.com" target="_blank" rel="noreferrer">Rates by Exchange Rate API</a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {products.map((product, index) => {
              const stock = inventory[product.id];
              const isSuppressor = product.id === 6;
              const availableColors = isSuppressor ? ['Black'] : ['Black', 'Translucent White', 'Translucent Red', 'Translucent Blue', 'Translucent Green'];
              const selectedColor = productColors[product.id];
              const getStockForColor = (c) => {
                if (!stock) return null;
                if (c === 'Black') return stock.black;
                if (c === 'Translucent White') return stock.transparent;
                if (c === 'Translucent Red') return stock.red;
                if (c === 'Translucent Blue') return stock.blue;
                if (c === 'Translucent Green') return stock.green;
                return null;
              };
              const selectedStock = selectedColor ? getStockForColor(selectedColor) : null;
              const outOfStock = selectedColor ? (selectedStock !== null && selectedStock <= 0) : false;
              const qty = productQtys[product.id] || 1;
              return (
              <motion.div
                key={product.id}
                data-testid={`gun-card-${product.id}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-gradient-to-br from-[#181818] to-[#181818]/50 rounded-3xl border-2 transition-all duration-500 overflow-hidden flex flex-col border-white/5 hover:border-[#FF0000] hover:shadow-[0_0_15px_rgba(255,0,0,0.6),0_0_30px_rgba(255,0,0,0.3),inset_0_0_15px_rgba(255,0,0,0.05)]">
                
                {/* Product Image */}
                {(() => {
                  const resolvedImg =
                    selectedColor === 'Translucent White' && product.imageWhite ? product.imageWhite :
                    selectedColor === 'Translucent Red'   && product.imageRed   ? product.imageRed   :
                    selectedColor === 'Translucent Blue'  && product.imageBlue  ? product.imageBlue  :
                    selectedColor === 'Translucent Green' && product.imageGreen ? product.imageGreen :
                    product.imageBlack ? product.imageBlack : product.image;
                  return product.id <= 3 ? (
                      <ProductSlideshow
                        key={`${product.id}-${selectedColor || 'default'}`}
                        productId={product.id}
                        productName={product.name}
                        image={resolvedImg}
                        upgradeImage={UPGRADE_IMAGES[product.id][selectedColor || 'Black']}
                      />
                    ) : (
                      <div className="overflow-hidden bg-transparent aspect-square w-4/5 mx-auto flex items-center justify-center p-4 mb-[-8px]">
                        {resolvedImg === null ? (
                          <div className="w-full h-full flex items-center justify-center bg-[#111]">
                            <span className="text-[#FF0000] text-2xl font-black tracking-widest uppercase">Coming Soon</span>
                          </div>
                        ) : (
                          <img src={resolvedImg} alt={product.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                    );
                })()}

                {/* Product Info */}
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-base sm:text-lg font-bold mb-1 text-white transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-[#C5C6C7] text-xs sm:text-sm mb-3 leading-relaxed">
                    {product.description}
                  </p>
                  {product.id === 4 && (
                    <p className="mb-3 rounded-lg border border-[#FF0000]/50 bg-[#FF0000]/10 px-3 py-2 text-xs sm:text-sm font-semibold text-white">
                      <span className="text-[#FF4040]">SPECIAL OFFER:</span> Comes with Free Lifetime VSteps
                    </p>
                  )}
                  
                  <div className="mt-auto space-y-2">
                    <div className="flex items-center justify-between">
                      <span data-testid={`gun-price-${product.id}`} className="text-lg sm:text-xl font-bold text-[#FF0000]">
                        {formatPrice((COLORED_VARIANTS.includes(selectedColor) && product.coloredPrice) ? product.coloredPrice : product.price)}
                      </span>
                      {stock && selectedColor && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${selectedStock > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {selectedStock > 0 ? `${selectedStock} in stock` : '0 stock'}
                        </span>
                      )}
                    </div>
                    
                    {/* Colors */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-[#C5C6C7] font-semibold whitespace-nowrap">Color</span>
                      {availableColors.map(color => {
                        const swatchClass = color === 'Black' ? 'bg-black border-white/40' : color === 'Translucent White' ? 'bg-white/80 border-white/40' : color === 'Translucent Red' ? 'bg-red-500 border-red-300' : color === 'Translucent Blue' ? 'bg-blue-500 border-blue-300' : 'bg-green-500 border-green-300';
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => handleColorChange(product.id, color)}
                            aria-pressed={selectedColor === color}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-all duration-200 ${productColors[product.id] === color ? 'border-[#FF0000] bg-[#FF0000]/10 text-white' : 'border-white/20 bg-[#0B0C10] text-[#C5C6C7] hover:border-white/40'}`}>
                            <span className={`w-3 h-3 rounded-full border ${swatchClass}`} />
                            {color}
                            {productColors[product.id] === color && <span className="text-[#FF0000]">✓</span>}
                          </button>
                        );
                      })}
                    </div>

                    {/* Quantity */}
                    <div className={`flex items-center gap-2 ${!selectedColor ? 'opacity-40 pointer-events-none' : ''}`}>
                      <span className="text-xs text-[#C5C6C7] font-semibold whitespace-nowrap">Qty</span>
                      <button
                        type="button"
                        onClick={() => handleQtyChange(product.id, -1, selectedStock)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-white/20 bg-[#0B0C10] text-white hover:border-white/40 transition-all">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-white font-bold text-sm">{qty}</span>
                      <button
                        type="button"
                        onClick={() => handleQtyChange(product.id, 1, selectedStock)}
                        disabled={selectedStock !== null && selectedStock !== undefined && qty >= selectedStock}
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-white/20 bg-[#0B0C10] text-white hover:border-white/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <Button
                      onClick={() => setShowTermsModal(true)}
                      variant="outline"
                      size="sm"
                      className="w-full border-2 border-white/90 bg-white/10 text-white hover:bg-white/20 text-xs font-semibold backdrop-blur-sm">
                      <FileText className="w-3 h-3 mr-1" />
                      See Terms & Conditions
                    </Button>
                    
                    <div className="pt-2">
                      <Button
                        onClick={() => !outOfStock && handleAddToCart(product)}
                        size="sm"
                        disabled={outOfStock || !ready}
                        className="w-full bg-[#FF0000] hover:bg-[#FF0000]/90 text-white font-semibold shadow-lg shadow-red-500/20 hover:shadow-red-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                        <ShoppingCart className="w-3 h-3 mr-1" />
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF0000]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </motion.div>
            );
          })}
          </div>
        </div>
      </section>

      {/* Terms and Conditions Modal */}
      <AnimatePresence>
        {showTermsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowTermsModal(false)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] rounded-2xl border-2 border-white/5 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
              style={{ filter: 'grayscale(0.3)' }}>
              
              <div className="bg-[#0d0d0d] border-b border-white/5 p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#FF0000]" />
                  <h2 className="text-2xl font-bold text-white">Terms & Conditions</h2>
                </div>
                <Button onClick={() => setShowTermsModal(false)} variant="ghost" size="icon" className="text-white hover:bg-white/10">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 space-y-6 text-[#C5C6C7]">
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">1. Product Information</h3>
                  <p className="text-sm leading-relaxed">All products listed are for recreational and entertainment purposes. By placing an order, you acknowledge that you have read and understood all product specifications, restrictions, and applicable laws in your jurisdiction.</p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">2. Shipping Restrictions</h3>
                  <p className="text-sm leading-relaxed mb-2">Black color variants cannot be shipped to the following states due to local regulations:</p>
                  <ul className="text-sm list-disc list-inside space-y-1 ml-4">
                    <li>Arkansas (AR)</li>
                    <li>California (CA)</li>
                    <li>Connecticut (CT)</li>
                    <li>Massachusetts (MA)</li>
                    <li>New Jersey (NJ)</li>
                    <li>New York (NY)</li>
                  </ul>
                  <p className="text-sm leading-relaxed mt-2"><strong className="text-white">Exception:</strong> The Black Suppressor attachment is available for shipping to all states, including the restricted states listed above.</p>
                  <p className="text-sm leading-relaxed mt-2">Translucent variants are available for shipping to all states unless otherwise restricted by law.</p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">3. Age & Legal Requirements</h3>
                  <p className="text-sm leading-relaxed">By purchasing, you confirm that you are of legal age (18+) and authorized to purchase these products in your state and jurisdiction. You are responsible for ensuring compliance with all local, state, and federal laws.</p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">4. Production & Shipping</h3>
                  <p className="text-sm leading-relaxed">Please allow up to 12 hours for production after your order is placed. Once your order has shipped, a tracking number will be sent to the email address provided at checkout.</p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">5. Returns & Refunds</h3>
                  <p className="text-sm leading-relaxed">All sales are final. We do not accept returns or provide refunds except in cases of manufacturing defects or shipping errors. Claims must be submitted within 14 days of delivery.</p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">6. Liability</h3>
                  <p className="text-sm leading-relaxed">V4llued Guns is not liable for any misuse of products. Users are solely responsible for safe handling, storage, and compliance with all applicable laws and regulations.</p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">7. Privacy & Data</h3>
                  <p className="text-sm leading-relaxed">Your personal information is protected and will only be used for order processing and shipping. We do not share your data with third parties except as required for delivery services.</p>
                </div>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-[#C5C6C7]">Last updated: February 25, 2026</p>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex justify-end">
                <Button onClick={() => setShowTermsModal(false)} className="bg-[#FF0000] hover:bg-[#FF0000]/90 text-white">Close</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="VSteps" className="h-6 sm:h-8 w-auto" />
            <span className="text-[#C5C6C7] text-xs sm:text-sm">© 2025 V4llued. All rights reserved.</span>
          </div>
          <a
            href="https://linktr.ee/v4llued"
            target="_self"
            rel="noopener noreferrer"
            data-testid="link-resources"
            className="text-[#C5C6C7] text-xs sm:text-sm hover:text-white transition-colors">
            Resources
          </a>
        </div>
      </footer>
    </div>
  );
}